import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import api, { getCsrfToken } from '../../../services/api';
import ThemeContext from '../../../ThemeContext';
import CaseFoldersSection from './CaseFolder/CaseFoldersSection';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import ClientConfig from '../../../../config/Clientconfig';
import AppConfig from '../../../../config/applicationconfig';
import { URL_SEGMENTS } from '../../../../config/Routesconfig';

const ViewCase = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const { username, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [caseData, setCaseData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCaseFolders, setShowCaseFolders] = useState(false);

  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customTypeValue, setCustomTypeValue] = useState('');
  const [savedCustomTypes, setSavedCustomTypes] = useState([]);
  const [isSavingCustomType, setIsSavingCustomType] = useState(false);

  // Typography from theme
  const fontFamily =
    theme?.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const titleFontSize = theme?.typography?.titleFontSize || 24;
  const bodyFontSize = theme?.typography?.bodyFontSize || 14;
  const smallFontSize = Math.max(bodyFontSize - 2, 10);

  const isNestedRoute =
    location.pathname.includes('/add-') ||
    location.pathname.includes('/edit-');

    const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role;
const isClientUser = userRole?.toLowerCase() === "client_user";

  // ─── CUSTOM TYPE SUBMIT ─────
  const handleCustomTypeSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const trimmedType = customTypeValue.trim();
    if (!trimmedType) return;

    const alreadyExists = savedCustomTypes.some(
      (type) => (type.name || '').trim().toLowerCase() === trimmedType.toLowerCase()
    );

    if (alreadyExists) {
      toast.error(`${displayText("\"")}${trimmedType}${displayText("\" session type is already available.")}`, {
        autoClose: 4000,
      });
      setShowCustomInput(false);
      setCustomTypeValue('');
      return;
    }

    try {
      setIsSavingCustomType(true);

      const payload = {
        case_id: parseInt(id, 10),
        custom_type: trimmedType,
        witness: `${trimmedType} Session`,
        date: new Date().toISOString().split('T')[0],
        location: 'Virtual',
        session_passcode: Math.floor(10000 + Math.random() * 90000).toString(),
        session_type: 'custom',
        introducing_party: ['N/A'],
        court_reporting_firm: 'N/A',
        schedule_video: false,
        zoom_tech: null,
        registrants: null,
        meeting_duration: null,
        meeting_time: null,
        meeting_timezone: null,
        details: null,
        status: 'pending',
      };

      await api.post(`/cases/${id}/custom-sessions`, payload);

      const customResponse = await api.get(`/cases/${id}/custom-sessions`);
      const customSessions = Array.isArray(customResponse.data)
        ? customResponse.data
        : customResponse.data.data || [];

      const uniqueTypesMap = new Map();
      customSessions.forEach((s) => {
        const key = (s.custom_type || '').trim().toLowerCase();
        if (key && !uniqueTypesMap.has(key)) {
          uniqueTypesMap.set(key, { id: s.id, name: s.custom_type, session: s });
        }
      });

      setSavedCustomTypes([...uniqueTypesMap.values()]);
      setShowCustomInput(false);
      setCustomTypeValue('');
      toast.success(`${displayText("\"")}${trimmedType}${displayText("\" session type created!")}`, { autoClose: 3000 });
    } catch (error) {
      console.error('Failed to create custom session:', error.response?.data || error);
      const backendErrors = error.response?.data?.errors;
      const formattedErrors = backendErrors
        ? Object.values(backendErrors).flat().join(', ')
        : error.response?.data?.message || error.message;
      toast.error(`Failed: ${formattedErrors}`, { autoClose: 5000 });
    } finally {
      setIsSavingCustomType(false);
    }
  };

  // ─── NAVIGATE TO SAVED CUSTOM TYPE 
  const handleSavedCustomTypeClick = (customType) => {
    navigate(
      `/${username}/dashboard/${URL_SEGMENTS.viewCase}/${id}/add-custom/${customType.session.id}`
    );
  };

  // ─── LOAD SAVED CUSTOM TYPES ON MOUNT ────────────────────────────
  useEffect(() => {
    const loadSavedCustomTypes = async () => {
      try {
        const response = await api.get(`/cases/${id}/custom-sessions`);
        const customSessions = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];

        const uniqueTypesMap = new Map();
        customSessions.forEach((s) => {
          const key = (s.custom_type || '').trim().toLowerCase();
          if (key && !uniqueTypesMap.has(key)) {
            uniqueTypesMap.set(key, { id: s.id, name: s.custom_type, session: s });
          }
        });
        setSavedCustomTypes([...uniqueTypesMap.values()]);
      } catch (error) {
        console.error('Failed to load custom types:', error);
        setSavedCustomTypes([]);
      }
    };
    loadSavedCustomTypes();
  }, [id]);

  // ─── FETCH ALL SESSIONS ────────────
  const fetchSessions = async () => {
    try {
      const [depositions, arbitrations, trials, mediations, witnessPreps, customSessions] =
        await Promise.all([
          api.get(`/cases/${id}/deposition-sessions`).catch(() => ({ data: [] })),
          api.get(`/cases/${id}/arbitrations`).catch(() => ({ data: [] })),
          api.get(`/cases/${id}/trials`).catch(() => ({ data: [] })),
          api.get(`/cases/${id}/mediations`).catch(() => ({ data: [] })),
          api.get(`/cases/${id}/witness-preps`).catch(() => ({ data: [] })),
          api.get(`/cases/${id}/custom-sessions`).catch(() => ({ data: [] })),
        ]);

      const normalizeAndFilter = (items, expectedType) =>
        (items || [])
          .filter((s) => (s.session_type || '').toLowerCase().trim() === expectedType)
          .map((s) => {
            let displayType = '';
            switch (expectedType) {
              case 'deposition':   displayType = ClientConfig.sessions.types.deposition.label;   break;
              case 'arbitration':  displayType = ClientConfig.sessions.types.arbitration.label;  break;
              case 'trial':        displayType = ClientConfig.sessions.types.trial.label;        break;
              case 'mediation':    displayType = ClientConfig.sessions.types.mediation.label;    break;
              case 'witness_prep': displayType = ClientConfig.sessions.types.witness_prep.label; break;
              case 'custom':       displayType = s.custom_type?.trim() || 'Custom Session';      break;
              default:             displayType = expectedType;
            }
            return { ...s, normalizedType: expectedType, displayType };
          });

      const allSessions = [
        ...normalizeAndFilter(depositions.data,   'deposition'),
        ...normalizeAndFilter(arbitrations.data,  'arbitration'),
        ...normalizeAndFilter(trials.data,        'trial'),
        ...normalizeAndFilter(mediations.data,    'mediation'),
        ...normalizeAndFilter(witnessPreps.data,  'witness_prep'),
        ...normalizeAndFilter(customSessions.data,'custom'),
      ];

      const uniqueSessionsMap = new Map();
      allSessions.forEach((s) => {
        uniqueSessionsMap.set(`${s.id}-${s.normalizedType}`, s);
      });
      setSessions([...uniqueSessionsMap.values()]);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  // ─── FETCH CASE ───
  useEffect(() => {
    const fetchCase = async () => {
      try {
        setIsLoading(true);
        await getCsrfToken();
        const caseResponse = await api.get(`/cases/${id}`);
        if (!caseResponse.data.data) throw new Error('No case data returned');
        setCaseData(caseResponse.data.data);
        if (!isNestedRoute) {
          fetchSessions();
        }
      } catch (error) {
        console.error('Failed to fetch case or sessions:', error);
        toast.error(
          `${displayText("Failed to fetch case details: ")}${error.response?.data?.message || error.message}${displayText("")}`,
          { autoClose: 3000 }
        );
        //  FIX: use URL_SEGMENTS.cases instead of hardcoded "cases"
        navigate(`/${username}/dashboard/${URL_SEGMENTS.cases}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCase();
  }, [id, username, navigate, theme.colors, location.pathname, isNestedRoute, fontFamily, bodyFontSize]);

  // ─── DELETE CASE ──
  const handleDelete = async () => {
    // ── Block sample case deletion 
    if (caseData?.is_sample) {
      toast.warning(displayText('The sample case cannot be deleted.'), { autoClose: 3000 });
      return;
    }

    if (window.confirm(displayText('Are you sure you want to delete this case?'))) {
      try {
        setIsLoading(true);
        await api.delete(`/cases/${id}`);
        toast.success(displayText('Case deleted successfully'), { autoClose: 3000 });
        navigate(`/${username}/dashboard/${URL_SEGMENTS.cases}`);
      } catch (error) {
        toast.error(
          `${displayText("Failed to delete case: ")}${error.response?.data?.message || error.message}${displayText("")}`,
          { autoClose: 3000 }
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  // ─── BUILD EDIT SESSION URL ────────
  // Centralised so it is easy to maintain
  const buildEditSessionUrl = (session) => {
    const base = `/${username}/dashboard/${URL_SEGMENTS.viewCase}/${id}`;
    switch (session.normalizedType) {
      case 'custom':       return `${base}/${URL_SEGMENTS.editCustom}/${session.id}`;
      case 'witness_prep': return `${base}/${URL_SEGMENTS.editWitnessPrep}/${session.id}`;
      case 'deposition':   return `${base}/${URL_SEGMENTS.editDeposition}/${session.id}`;
      case 'arbitration':  return `${base}/${URL_SEGMENTS.editArbitration}/${session.id}`;
      case 'trial':        return `${base}/${URL_SEGMENTS.editTrial}/${session.id}`;
      case 'mediation':    return `${base}/${URL_SEGMENTS.editMediation}/${session.id}`;
      default:             return `${base}/edit-${session.normalizedType}/${session.id}`;
    }
  };

  // ─── LOADING STATE 
  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: '40px', height: '40px',
            border: `4px solid ${theme.colors.primary}`,
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div
        style={{
          textAlign: 'center', padding: '2rem',
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          fontFamily, fontSize: `${bodyFontSize}px`,
        }}
      >{displayText(" No case data available. ")}</div>
    );
  }

  // ─── NESTED ROUTE (add- / edit- forms rendered via <Outlet />) ────
  if (isNestedRoute) {
    return (
      <div
        style={{
          fontFamily, fontSize: `${bodyFontSize}px`,
          minHeight: '100vh',
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
        }}
      >
        <ToastContainer
          position="top-right" autoClose={3000}
          hideProgressBar={false} newestOnTop={false}
          closeOnClick rtl={false} pauseOnFocusLoss
          draggable pauseOnHover theme="light"
          toastStyle={{
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            fontFamily, fontSize: `${bodyFontSize}px`,
          }}
        />
        <Outlet />
      </div>
    );
  }

  // ─── MAIN VIEW ────
  return (
    <div
      style={{
        fontFamily, fontSize: `${bodyFontSize}px`,
        minHeight: '100vh',
        backgroundColor: theme.colors.bgPrimary,
        color: theme.colors.textPrimary,
        padding: '1.5rem 0',
      }}
    >
      <ToastContainer
        position="top-right" autoClose={3000}
        hideProgressBar={false} newestOnTop={false}
        closeOnClick rtl={false} pauseOnFocusLoss
        draggable pauseOnHover theme="light"
        toastStyle={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          fontFamily, fontSize: `${bodyFontSize}px`,
        }}
      />

      <div
        style={{
          width: '100%', maxWidth: 'none', margin: '0 auto',
          backgroundColor: theme.colors.bgPrimary,
          borderRadius: '12px', padding: '1.5rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.colors.borderColor || '#ddd'}`,
        }}
      >
        {/* ── HEADER ─── */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            marginBottom: '1.5rem', padding: '1rem',
            borderRadius: '6px',
            border: `1px solid ${theme.colors.borderColor}`,
            backgroundColor: theme.colors.bgPrimary,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <i
              className="bi bi-folder-fill"
              style={{ fontSize: `${bodyFontSize}px`, color: theme.colors.primary }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {/*  FIX: use URL_SEGMENTS.cases */}
              <Link
                to={`/${username}/dashboard/${URL_SEGMENTS.cases}`}
                style={{
                  fontSize: `${bodyFontSize}px`, color: theme.colors.textPrimary,
                  fontWeight: 'bold', textDecoration: 'none',
                  transition: 'color 0.3s ease',
                }}
                onMouseOver={(e) => (e.target.style.color = theme.colors.primary)}
                onMouseOut={(e)  => (e.target.style.color = theme.colors.textPrimary)}
              >
                {ClientConfig.cases.managementLabel}
              </Link>
              <span style={{ fontSize: `${bodyFontSize}px`, color: theme.colors.textPrimary, fontWeight: 'bold' }}>
                / {caseData.case_name}
              </span>
            </div>
          </div>

          {/*  FIX: use URL_SEGMENTS.cases */}
          <button
            onClick={() => navigate(`/${username}/dashboard/${URL_SEGMENTS.cases}`)}
            style={{
              background: theme.gradients.secondary,
              color: theme.colors.textWhite,
              fontSize: `${smallFontSize}px`,
              padding: '0.5rem 0.75rem', borderRadius: '8px',
              border: 'none', transition: 'opacity 0.3s ease',
              boxShadow: 'none',
              width: window.innerWidth < 600 ? '100%' : 'auto',
              minWidth: '120px', fontFamily,
            }}
            onMouseOver={(e) => (e.target.style.opacity = '0.9')}
            onMouseOut={(e)  => (e.target.style.opacity = '1')}
          >
            Back to {ClientConfig.cases.managementLabel}
          </button>
        </div>

        {caseData?.is_sample && (
          <div
            style={{
              backgroundColor : '#fff3cd',
              border          : '1px solid #ffc107',
              borderRadius    : '8px',
              padding         : '0.75rem 1rem',
              marginBottom    : '1rem',
              display         : 'flex',
              alignItems      : 'center',
              gap             : '0.75rem',
              fontSize        : `${smallFontSize}px`,
              fontFamily,
              color           : '#664d03',
            }}
          >
            <i
              className="bi bi-info-circle-fill"
              style={{ fontSize: '1rem', color: '#ffc107', flexShrink: 0 }}
            />
            <span>
              <strong>{displayText("Sample / Demo Case")}</strong>{displayText(" — This is your practice environment. Add sessions, folders, and documents freely. The case itself cannot be deleted. ")}</span>
          </div>
        )}

        {/* ── CASE TITLE & ACTION BUTTONS ────────────────────────── */}
       <div
  style={{
    display: 'flex',
    flexDirection: window.innerWidth < 600 ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
    marginBottom: '1.5rem',
  }}
>
  <h3
    style={{
      fontSize: `${titleFontSize}px`,
      color: theme.colors.textPrimary,
      margin: 0,
    }}
  >
    {ClientConfig.cases.label} #{caseData.id}: {caseData.case_name}
  </h3>

  <div
    style={{
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      justifyContent: window.innerWidth < 600 ? 'stretch' : 'flex-end',
    }}
  >
    {/*  Wrap BOTH dropdowns under ONE condition */}
    {!isClientUser && (
      <>
        {/* ── Case Options Dropdown ── */}
        <div className="dropdown">
          <button
            className="btn dropdown-toggle"
            type="button"
            id="caseOptionsDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              fontSize: `${smallFontSize}px`,
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: 'none',
              transition: 'opacity 0.3s ease',
              boxShadow: 'none',
              width: '160px',
              fontFamily,
            }}
          >
            {ClientConfig.cases.label} Options
          </button>

          <ul
            className="dropdown-menu"
            aria-labelledby="caseOptionsDropdown"
            style={{
              backgroundColor: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              fontSize: `${smallFontSize}px`,
              fontFamily,
            }}
          >
            <li>
              <Link
                className="dropdown-item"
                to={`/${username}/dashboard/${URL_SEGMENTS.editCase}/${id}`}
                style={{
                  color: theme.colors.textPrimary,
                  textDecoration: 'none',
                  fontFamily,
                }}
              >
                Edit {AppConfig.casename}
              </Link>
            </li>

            <li>
              <button
                className="dropdown-item"
                onClick={handleDelete}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.colors.danger,
                  width: '100%',
                  textAlign: 'left',
                  fontSize: `${smallFontSize}px`,
                  fontFamily,
                }}
              >
                Delete {ClientConfig.cases.label}
              </button>
            </li>
          </ul>
        </div>

        {/* ── Session Category Dropdown ── */}
        <div className="dropdown">
          <button
            className="btn dropdown-toggle"
            type="button"
            id="addSessionDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              fontSize: `${smallFontSize}px`,
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: 'none',
              transition: 'opacity 0.3s ease',
              boxShadow: 'none',
              width: '160px',
              fontFamily,
            }}
          >
            {ClientConfig.cases.label} Category
          </button>

          <ul
            className="dropdown-menu"
            aria-labelledby="addSessionDropdown"
            style={{
              backgroundColor: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              fontSize: `${smallFontSize}px`,
              fontFamily,
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            <li>
              <Link
                className="dropdown-item"
                to={`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${id}/${URL_SEGMENTS.addDeposition}`}
              >
                {ClientConfig.sessions.types.deposition.label}
              </Link>
            </li>

            <li>
              <Link
                className="dropdown-item"
                to={`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${id}/${URL_SEGMENTS.addWitnessPrep}`}
              >
                {ClientConfig.sessions.types.witness_prep.label}
              </Link>
            </li>

            <li>
              <Link
                className="dropdown-item"
                to={`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${id}/${URL_SEGMENTS.addTrial}`}
              >
                {ClientConfig.sessions.types.trial.label}
              </Link>
            </li>

            <li>
              <Link
                className="dropdown-item"
                to={`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${id}/${URL_SEGMENTS.addArbitration}`}
              >
                {ClientConfig.sessions.types.arbitration.label}
              </Link>
            </li>

            <li>
              <Link
                className="dropdown-item"
                to={`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${id}/${URL_SEGMENTS.addMediation}`}
              >
                {ClientConfig.sessions.types.mediation.label}
              </Link>
            </li>

            {/* Saved Custom Types */}
            {savedCustomTypes.map((customType) => (
              <li key={customType.id}>
                <button
                  className="dropdown-item"
                  onClick={() => handleSavedCustomTypeClick(customType)}
                >
                  {customType.name}
                </button>
              </li>
            ))}

            <li>
              <button
                className="dropdown-item"
                onClick={() => setShowCustomInput(true)}
                style={{ color: theme.colors.primary }}
              >
                <i className="bi bi-plus-circle me-2" />
                New Custom Type
              </button>
            </li>
          </ul>
        </div>
      </>
    )}
  </div>
</div>
        {/* ── CUSTOM TYPE MODAL ───────────────────────────────────── */}
        {showCustomInput && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 10000, fontFamily,
            }}
            onClick={() => { setShowCustomInput(false); setCustomTypeValue(''); }}
          >
            <div
              style={{
                backgroundColor: theme.colors.bgPrimary,
                borderRadius: '12px', padding: '2rem',
                maxWidth: '400px', width: '90%',
                border: `1px solid ${theme.colors.borderColor}`,
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h4 style={{ color: theme.colors.textPrimary, margin: '0 0 1rem 0', fontSize: `${titleFontSize}px` }}>
                Create New {AppConfig.casename} Type
              </h4>
              <form onSubmit={handleCustomTypeSubmit}>
                <div style={{ position: 'relative', marginBottom: '1rem' }}>
                  <input
                    type="text"
                    value={customTypeValue}
                    onChange={(e) => setCustomTypeValue(e.target.value)}
                    placeholder={displayText("e.g., Mock Trial, Discovery Session, etc.")}
                    disabled={isSavingCustomType}
                    style={{
                      width: '100%', padding: '0.75rem 1rem',
                      border: `2px solid ${theme.colors.borderColor}`,
                      borderRadius: '8px', fontSize: `${bodyFontSize}px`,
                      backgroundColor: theme.colors.bgSecondary,
                      color: theme.colors.textPrimary, fontFamily,
                      outline: 'none', transition: 'border-color 0.3s ease',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = theme.colors.primary)}
                    onBlur={(e)  => (e.target.style.borderColor = theme.colors.borderColor)}
                    autoFocus
                  />
                  {isSavingCustomType && (
                    <div style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)' }}>
                      <div
                        style={{
                          width: '16px', height: '16px',
                          border: `2px solid ${theme.colors.primary}`,
                          borderTop: '2px solid transparent',
                          borderRadius: '50%', animation: 'spin 1s linear infinite',
                        }}
                      />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => { setShowCustomInput(false); setCustomTypeValue(''); }}
                    disabled={isSavingCustomType}
                    style={{
                      padding: '0.5rem 1rem', background: 'none',
                      border: `1px solid ${theme.colors.borderColor}`,
                      borderRadius: '6px', color: theme.colors.textPrimary,
                      fontSize: `${smallFontSize}px`, fontFamily,
                      cursor: isSavingCustomType ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!customTypeValue.trim() || isSavingCustomType}
                    style={{
                      padding: '0.5rem 1rem',
                      background: (customTypeValue.trim() && !isSavingCustomType)
                        ? theme.gradients.primary
                        : theme.colors.borderColor,
                      color: theme.colors.textWhite, border: 'none',
                      borderRadius: '6px', fontSize: `${smallFontSize}px`,
                      fontFamily,
                      cursor: (customTypeValue.trim() && !isSavingCustomType) ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {isSavingCustomType ? 'Creating...' : `Create ${AppConfig.casename}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── CASE FOLDERS (COLLAPSIBLE) ─*/}
        <div style={{ marginBottom: '1.5rem' }}>
          <button
            onClick={() => setShowCaseFolders(!showCaseFolders)}
            style={{
              width: '100%', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between',
              background: theme.gradients.secondary, color: theme.colors.textWhite,
              padding: '0.75rem', borderRadius: '5px', border: 'none',
              fontSize: `${bodyFontSize}px`, cursor: 'pointer',
              transition: 'opacity 0.3s ease', fontFamily,
            }}
            onMouseOver={(e) => (e.target.style.opacity = '0.9')}
            onMouseOut={(e)  => (e.target.style.opacity = '1')}
          >
            <span>{ClientConfig.cases.label} {ClientConfig.folders.pluralLabel}</span>
            <i className={`bi bi-chevron-${showCaseFolders ? 'up' : 'down'}`} />
          </button>
          {showCaseFolders && (
            <div style={{ marginTop: '1rem' }}>
              <CaseFoldersSection
  caseId={id}
  username={username}
  sessions={sessions}
/>
            </div>
          )}
        </div>

        {/* ── SESSIONS TABLE HEADER ── */}
        <div
  style={{
    background: theme.gradients.secondary, color: theme.colors.textWhite,
    padding: '0.5rem', borderRadius: '5px',
    marginBottom: '1rem', fontSize: `${smallFontSize}px`, fontFamily,
    display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
  }}
>
  {/* Total count */}
  <span>{displayText("Sessions (")}{sessions.length})</span>

  {/* Status breakdown badges — only show if there are sessions */}
  {sessions.length > 0 && (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>

      {/* PENDING badge */}
      {sessions.filter(s => s.status === 'pending').length > 0 && (
        <span style={{
          backgroundColor: '#fef3c7',
          color: '#92400e',
          borderRadius: '4px',
          padding: '2px 8px',
          fontSize: `${smallFontSize}px`,
          fontFamily,
        }}>
          {sessions.filter(s => s.status === 'pending').length} Pending
        </span>
      )}

      {/* STARTED / ACTIVE badge */}
      {sessions.filter(s => s.status === 'started').length > 0 && (
        <span style={{
          backgroundColor: '#ede9fe',
          color: '#5b21b6',
          borderRadius: '4px',
          padding: '2px 8px',
          fontSize: `${smallFontSize}px`,
          fontFamily,
        }}>
          {sessions.filter(s => s.status === 'started').length} Active
        </span>
      )}

      {/* SCHEDULED badge */}
      {sessions.filter(s => s.status === 'scheduled').length > 0 && (
        <span style={{
          backgroundColor: '#dbeafe',
          color: '#1e40af',
          borderRadius: '4px',
          padding: '2px 8px',
          fontSize: `${smallFontSize}px`,
          fontFamily,
        }}>
          {sessions.filter(s => s.status === 'scheduled').length} Scheduled
        </span>
      )}

      {/* COMPLETED / FINISHED badge */}
      {(sessions.filter(s => s.status === 'completed' || s.status === 'finished').length) > 0 && (
        <span style={{
          backgroundColor: '#d1fae5',
          color: '#065f46',
          borderRadius: '4px',
          padding: '2px 8px',
          fontSize: `${smallFontSize}px`,
          fontFamily,
        }}>
          {sessions.filter(s => s.status === 'completed' || s.status === 'finished').length} Completed
        </span>
      )}

    </div>
  )}
</div>

        {/* ── SESSIONS TABLE ─*/}
        <div
          style={{
            overflowX: 'auto', borderRadius: '10px',
            border: `1px solid ${theme.colors.borderColor}`,
            backgroundColor: theme.colors.bgPrimary,
          }}
        >
          <table
            style={{
              width: '100%', borderCollapse: 'collapse',
              fontSize: `${smallFontSize}px`, fontFamily,
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: theme.colors.bgSecondary,
                  color: theme.colors.textPrimary,
                  borderBottom: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Volume</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>{ClientConfig.sessions.category.label}</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Owner</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Location</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length > 0 ? (
                sessions.map((session) => (
                  <tr
                    key={`${session.session_type}-${session.id}`}
                    style={{
                      borderBottom: `1px solid ${theme.colors.borderColor}`,
                      backgroundColor: theme.colors.bgSecondary,
                    }}
                  >
                    <td style={{ padding: '0.75rem' }}>{session.id || 'None'}</td>
                    <td style={{ padding: '0.75rem' }}>
                      {session.date ? new Date(session.date).toLocaleDateString() : 'None'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {session.name || session.witness || 'None'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>{session.volume || 'None'}</td>
                    <td style={{ padding: '0.75rem' }}>{session.displayType || '-'}</td>
                    <td style={{ padding: '0.75rem' }}>{session.status || 'Pending'}</td>
                    <td style={{ padding: '0.75rem' }}>{session.introducing_party || 'None'}</td>
                    <td style={{ padding: '0.75rem' }}>{session.location || 'None'}</td>
                    <td style={{ padding: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                      {/* View Session */}
                      <Link
                        to={`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${id}/${URL_SEGMENTS.viewSession}/${session.id}`}
                        style={{
                          color: theme.colors.primary, textDecoration: 'none',
                          fontFamily, padding: '0.25rem 0.5rem',
                          borderRadius: '4px', transition: 'background-color 0.2s',
                        }}
                        onMouseOver={(e) => (e.target.style.backgroundColor = theme.colors.bgSecondary)}
                        onMouseOut={(e)  => (e.target.style.backgroundColor = 'transparent')}
                      >
                        View
                      </Link>

                      {/*
                         FIX: use buildEditSessionUrl() which uses URL_SEGMENTS for every type.
                        Previously used a ternary that hardcoded "edit-witnessprep",
                        "edit-custom", "edit-{normalizedType}" — all inconsistent.
                      */}
                      {!isClientUser && (
                      <Link
                        to={buildEditSessionUrl(session)}
                        style={{
                          color: theme.colors.primary, textDecoration: 'none',
                          fontFamily, padding: '0.25rem 0.5rem',
                          borderRadius: '4px', transition: 'background-color 0.2s',
                        }}
                        onMouseOver={(e) => (e.target.style.backgroundColor = theme.colors.bgSecondary)}
                        onMouseOut={(e)  => (e.target.style.backgroundColor = 'transparent')}
                      >
                        Edit
                      </Link>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      padding: '2rem', textAlign: 'center',
                      backgroundColor: theme.colors.bgSecondary,
                    }}
                  >
                    <i
                      className="bi bi-inbox"
                      style={{
                        fontSize: '3rem', color: theme.colors.borderColor,
                        display: 'block', marginBottom: '1rem',
                      }}
                    />{displayText(" No sessions available. Create your first session above! ")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default ViewCase;